import WebsiteFooter from "common/layout/websiteFooter";
import { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useGetArticles from "../hooks/useGetArticles";
import { toast } from "react-toastify";
import { ArticlesServiceImpl } from "../../domain/services/articlesService";
import { BlogHeader } from "../components/BlogHeader";
import { BlogHero } from "../components/BlogHero";
import { TopArticles } from "../components/TopArticles";
import { FundraiserSection } from "../components/FundraiserSection";
import { RecentBlogs } from "../components/RecentBlogs";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { Pagination } from "../components/Pagination";

const ArticlesService = new ArticlesServiceImpl();

gsap.registerPlugin(ScrollTrigger);

export default function BlogPage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const introTitleRef = useRef<HTMLParagraphElement>(null);
  const introSubtitleRef = useRef<HTMLParagraphElement>(null);
  const fundraiserSectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLParagraphElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const illustrationRef = useRef<HTMLImageElement>(null);
  const topArticlesSectionRef = useRef<HTMLDivElement>(null);
  const topArticlesTitleRef = useRef<HTMLParagraphElement>(null);
  const navigationButtonsRef = useRef<HTMLDivElement>(null);
  const desktopCarouselRef = useRef<HTMLDivElement>(null);
  const mobileCarouselRef = useRef<HTMLDivElement>(null);
  const carouselItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const mobileCarouselItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const recentBlogsSectionRef = useRef<HTMLDivElement>(null);
  const recentBlogsTitleRef = useRef<HTMLParagraphElement>(null);
  const blogsGridRef = useRef<HTMLDivElement>(null);
  const blogItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const { data, isLoading, error, refetch } = useGetArticles();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const totalPages = data?.pages?.[0]?.meta?.pagination?.pageCount || 1;
  const totalArticles = data?.pages?.[0]?.meta?.pagination?.total || 0;
  const currentPageArticles = data?.pages?.[0]?.data?.length || 0;
  const hasMoreArticlesInPage = currentPageArticles < totalArticles;

  const articlesAvailable =
    data?.pages && data.pages[0]?.data && data.pages[0].data.length >= 1;

  useEffect(() => {
    if (error) {
      toast.error(
        ({ closeToast }) => (
          <div className="flex items-center justify-between gap-2">
            <span>{error?.message ?? ""}</span>
            <span
              onClick={() => {
                refetch();
                closeToast();
              }}
              className="text-sm cursor-pointer text-black underline hover:text-gray-700"
            >
              Reload
            </span>
          </div>
        ),
        {
          autoClose: false,
          closeOnClick: false,
        },
      );
    }
  }, [error, refetch]);

  const handlePageClick = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    refetch();
  };

  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await ArticlesService.loadMoreArticles(
        currentPage,
        currentPageArticles,
      );
      if (data?.pages?.[0]) {
        data.pages[0].data = response.data;
        data.pages[0].meta.pagination.pageSize =
          response.meta.pagination.pageSize;
      }
    } catch (error) {
      console.error("Error loading more articles:", error);
      toast.error("Failed to load more articles");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleNext = () => {
    if (!articlesAvailable) return;

    setCurrentIndex((prevIndex) =>
      prevIndex + 1 >= (data?.pages.flatMap((page) => page.data).length || 0)
        ? 0
        : prevIndex + 1,
    );
  };

  const handlePrev = () => {
    if (!articlesAvailable) return;

    setCurrentIndex((prevIndex) =>
      prevIndex - 1 < 0
        ? (data?.pages.flatMap((page) => page.data).length || 0) - 1
        : prevIndex - 1,
    );
  };

  const reversedArticles = articlesAvailable
    ? data?.pages.flatMap((page) => page.data).reverse()
    : [];

  return (
    <>
      <BlogHeader
        introTitleRef={introTitleRef}
        introSubtitleRef={introSubtitleRef}
      />
      <BlogHero />
      <TopArticles
        articles={reversedArticles}
        isLoading={isLoading}
        currentIndex={currentIndex}
        onPrev={handlePrev}
        onNext={handleNext}
        topArticlesSectionRef={topArticlesSectionRef}
        topArticlesTitleRef={topArticlesTitleRef}
        navigationButtonsRef={navigationButtonsRef}
        desktopCarouselRef={desktopCarouselRef}
        mobileCarouselRef={mobileCarouselRef}
        carouselItemsRef={carouselItemsRef}
        mobileCarouselItemsRef={mobileCarouselItemsRef}
      />
      <FundraiserSection
        fundraiserSectionRef={fundraiserSectionRef}
        headingRef={headingRef}
        textRef={textRef}
        buttonRef={buttonRef}
        illustrationRef={illustrationRef}
      />
      <RecentBlogs
        articles={reversedArticles}
        isLoading={isLoading}
        recentBlogsSectionRef={recentBlogsSectionRef}
        recentBlogsTitleRef={recentBlogsTitleRef}
        blogsGridRef={blogsGridRef}
        blogItemsRef={blogItemsRef}
      />
      {!isLoading && articlesAvailable && hasMoreArticlesInPage && (
        <LoadMoreButton isLoading={isLoadingMore} onClick={handleLoadMore} />
      )}
      {!isLoading && articlesAvailable && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageClick}
        />
      )}
      <div className="h-[100px] bg-[#022928]"></div>
      <div className="mt-[100px] lg:mx-[10px]">
        <WebsiteFooter />
      </div>
    </>
  );
}
